import api from '@/lib/axios';

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  is_default: boolean;
  plan_code?: string;
  status?: string;
  role_code?: string; // User's role in this workspace (from workspace_members.role_code)
}

export interface CreateWorkspaceRequest {
  name: string;
  plan_code?: string;
  description?: string;
}

export interface CreateWorkspaceResponse {
  workspace: Workspace;
}

export async function fetchMyWorkspaces(): Promise<Workspace[]> {
  const res = await api.get<Workspace[]>('/workspaces/my');
  return res.data || [];
}

export async function createWorkspace(data: CreateWorkspaceRequest): Promise<CreateWorkspaceResponse> {
  const res = await api.post<CreateWorkspaceResponse>('/workspaces', {
    name: data.name,
    plan_code: data.plan_code || 'trial',
    description: data.description,
  });
  return res.data;
}

/**
 * Fetch enabled modules for a workspace
 * 
 * @param workspaceId - The workspace ID
 * @returns Array of enabled module keys
 */
export async function fetchWorkspaceModules(workspaceId: string): Promise<string[]> {
  try {
    const response = await api.get<string[]>(`/workspaces/${workspaceId}/modules`);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error: any) {
    // 404 is expected when endpoint doesn't exist yet
    // Only log non-404 errors for debugging
    if (error.response?.status === 404) {
      console.warn(`Modules endpoint not available for workspace ${workspaceId}`);
      return [];
    }
    // For other errors, log but don't throw - return empty array for graceful degradation
    console.error('[fetchWorkspaceModules] Error:', error);
    return [];
  }
}

/**
 * Update a single module's enabled state for a workspace
 * 
 * @param workspaceId - The workspace ID
 * @param moduleKey - The module key to update
 * @param enabled - Whether the module should be enabled
 * @returns Updated modules array
 */
export async function updateWorkspaceModule(
  workspaceId: string,
  moduleKey: string,
  enabled: boolean
): Promise<string[]> {
  try {
    const response = await api.put<string[]>(`/workspaces/${workspaceId}/modules`, {
      module: moduleKey,
      enabled: enabled,
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error: any) {
    console.error('[updateWorkspaceModule] Error:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to update workspace module');
  }
}

/**
 * Fetch the current user's role in a workspace
 * 
 * @param workspaceId - The workspace ID
 * @returns The user's role_code in the workspace, or null if not found
 */
export async function fetchWorkspaceRole(workspaceId: string): Promise<string | null> {
  try {
    const response = await api.get<{ role_code: string | null }>(`/workspaces/${workspaceId}/my-role`);
    return response.data?.role_code ?? null;
  } catch (error: any) {
    // 404 is expected when endpoint doesn't exist or user is not a member
    // Only log non-404 errors for debugging
    if (error.response?.status !== 404) {
      console.error('[fetchWorkspaceRole] Error:', error);
    }
    // Return null for all errors (graceful degradation)
    // This allows the UI to work even when the endpoint is not available
    return null;
  }
}
